import L from 'leaflet';
import { EventBus } from '../../core/EventBus.js';
import { GameState } from '../../core/GameState.js';
import { calculateRoute, initSeaRoutes } from '../../utils/seaRoutes.js';
import { haversineDistance, normalizePolylineForDateline } from '../../utils/math.js';
import { CARGO_TYPES } from '../../config/cargoTypes.js';
import { REGION_COLORS } from '../../config/ports.js';
import { Modal } from '../components/Modal.js';
import { showConfirm } from '../components/dialogs.js';
import { escapeHtml } from '../../utils/formatting.js';

const PORT_SIZES = ['tiny', 'small', 'medium', 'large', 'major'];
const PORT_REGIONS = Object.keys(REGION_COLORS);

// Cargo-handling infrastructure a port can have — see src/entities/Port.js's
// CARGO_CATEGORY_TERMINAL_KEYS/canHandleCargo() for the runtime enforcement
// this data feeds (TradeSystem hard-blocks trading a category the port has
// no terminal for). `category` matches cargoTypes.js's `category` field
// exactly, used both to render the checklist and to validate produced/
// demanded goods against the chosen terminals on save.
const PORT_TERMINALS = [
  { field: 'hasBulkLiquidTerminal',   label: 'Bulk Liquid',  category: 'bulk_liquid' },
  { field: 'hasBulkDryTerminal',      label: 'Bulk Dry',     category: 'bulk_dry' },
  { field: 'hasContainerTerminal',    label: 'Container',    category: 'container' },
  { field: 'hasRefrigeratedTerminal', label: 'Refrigerated', category: 'refrigerated' },
  { field: 'hasRoRoTerminal',         label: 'RoRo',         category: 'ro_ro' }
];

/**
 * Admin panel for managing sea route waypoints.
 * Google Maps-style interaction:
 * - Drag waypoints to move them
 * - Drag from connection lines to insert new waypoints
 * - Right-click waypoint with 2 connections: remove and reconnect neighbors
 * - Right-click waypoint with >2 connections: remove waypoint and all its connections
 */
export class AdminPanel {
  constructor(mapLayers) {
    this.mapLayers = mapLayers;
    this.map = mapLayers.map;

    this.waypoints = [];
    this.connections = [];
    this.waypointMarkers = new Map();
    this.connectionLines = new Map(); // key: "id1|id2" -> { line, wp1, wp2 }

    // Reference/editable port layer — raw assets/data/ports.json rows (NOT
    // GameState Port entities; see _loadPorts()), so dragging one only ever
    // mutates its own .lat/.lng and nothing else about it.
    this.ports = [];
    this.portMarkers = new Map(); // portId -> marker[3] (world-copy offsets)
    this.hasUnsavedPortChanges = false;

    this.mode = 'edit'; // edit, connect, test_route
    this.selectedWaypoint = null;
    this.connectStart = null;
    this.testRouteStart = null;
    this.testRouteLine = null;

    // Undo history — shared between waypoint AND port edits (one Ctrl+Z
    // history for the whole panel); see _saveState()'s `kind` param for how
    // the two independent "unsaved changes" indicators stay accurate.
    this.undoStack = [];
    this.maxUndoSteps = 50;
    this.hasUnsavedChanges = false;

    // Create custom panes so waypoints always render above connection
    // lines, and ports above the regular (non-admin) port markers
    // (MapLayers.js's default 'ports' layer group, which renders in
    // Leaflet's default overlayPane at z-index 400) but below both of
    // those — so a waypoint placed right at a port still shows clearly on
    // top of it, matching the whole point of this layer (see it well
    // enough to drag it under the waypoint, not obscure the waypoint).
    //
    // All three sit ABOVE Leaflet's default markerPane (600 — where plain
    // L.marker() calls with no explicit `pane` land, which is what
    // ShipMarker.js/OtherShipMarker.js use for every ship icon). Found
    // live: at the old z-index 401-403, a ship docked at a port sat
    // visually and interactively on TOP of that port's admin ring —
    // clicking or dragging the ring there hit the ship's polygon instead
    // and silently did nothing, since ships default to a much higher pane.
    // Any port with a ship docked at it (every player's own starting port,
    // at minimum) was effectively unreachable for editing.
    if (!this.map.getPane('adminPortPane')) {
      this.map.createPane('adminPortPane');
      this.map.getPane('adminPortPane').style.zIndex = 601;
    }
    if (!this.map.getPane('adminConnectionPane')) {
      this.map.createPane('adminConnectionPane');
      this.map.getPane('adminConnectionPane').style.zIndex = 602;
    }
    if (!this.map.getPane('adminWaypointPane')) {
      this.map.createPane('adminWaypointPane');
      this.map.getPane('adminWaypointPane').style.zIndex = 603;
    }

    this.visible = false;
    this._createUI();
    this._bindEvents();
  }

  _createUI() {
    const panel = document.createElement('div');
    panel.id = 'admin-panel';
    panel.className = 'admin-panel collapsed';
    panel.style.display = 'none';
    panel.innerHTML = `
      <div class="admin-header">
        <h4>🗺️ Route Admin</h4>
        <button class="btn btn-sm" id="admin-toggle">▼</button>
      </div>
      <div class="admin-body">
        <div class="admin-section">
          <label>Mode:</label>
          <div class="admin-modes">
            <button class="btn btn-sm active" data-mode="edit">Edit</button>
            <button class="btn btn-sm" data-mode="connect">Connect</button>
            <button class="btn btn-sm" data-mode="test_route">Test Route</button>
          </div>
        </div>
        <div class="admin-help">
          <div id="help-edit" class="help-text">
            <b>Edit mode:</b><br>
            • Click waypoint to select (green)<br>
            • Click map to add connected waypoint<br>
            • Drag waypoint to move (drop on another to merge)<br>
            • Drag line to insert waypoint<br>
            • Right-click waypoint to delete<br>
            • Double-click map for standalone waypoint<br>
            • Shift+drag to box-select &amp; merge waypoints
          </div>
          <div id="help-connect" class="help-text" style="display:none">
            <b>Connect mode:</b><br>
            • Click two waypoints to connect them
          </div>
          <div id="help-test" class="help-text" style="display:none">
            <b>Test mode:</b><br>
            • Click two ports to test routing
          </div>
        </div>
        <div class="admin-section">
          <span id="admin-stats">0 waypoints, 0 connections</span>
          <span id="admin-unsaved" class="admin-unsaved" style="display:none"> (unsaved changes)</span>
        </div>
        <div class="admin-actions">
          <button class="btn btn-sm btn-success" id="admin-save">Save</button>
          <button class="btn btn-sm" id="admin-undo" title="Ctrl+Z">Undo</button>
          <button class="btn btn-sm" id="admin-reload">Reload</button>
          <button class="btn btn-sm" id="admin-export">Export</button>
          <button class="btn btn-sm" id="admin-clear-test">Clear Test</button>
        </div>
        <div class="admin-section">
          <label>Ports (drag the red ring onto the real harbor location):</label>
          <div class="admin-help">
            <div class="help-text">
              Ports render for reference/editing wherever ports.json currently
              has them — often a city-center coordinate, not the actual
              harbor. Drag a ring to correct its position, or click one
              (outside Test Route mode) to edit every field. Takes effect on
              the next server restart, same as every other port field.
            </div>
          </div>
          <span id="admin-port-stats">0 ports loaded</span>
          <span id="admin-ports-unsaved" class="admin-unsaved" style="display:none"> (unsaved changes)</span>
          <div class="admin-actions">
            <select id="admin-port-picker" style="flex:1;min-width:140px"></select>
            <button class="btn btn-sm" id="admin-edit-port">Edit</button>
          </div>
          <div class="admin-help">
            <div class="help-text">
              Clicking a port's ring on the map works too, but a nearby
              waypoint or ship often sits on the exact same pixel and takes
              the click instead — the picker above always reaches the right
              one.
            </div>
          </div>
          <div class="admin-actions">
            <button class="btn btn-sm btn-success" id="admin-save-ports">Save Ports</button>
            <button class="btn btn-sm" id="admin-add-port">+ Add Port</button>
          </div>
        </div>
        <div class="admin-section">
          <label>Test Route:</label>
          <div id="admin-route-info" class="admin-route-info">Select test mode, then click two ports</div>
        </div>
      </div>
    `;
    document.body.appendChild(panel);

    // Create layers
    this.waypointLayer = L.layerGroup();
    this.connectionLayer = L.layerGroup();
    this.testRouteLayer = L.layerGroup();
    this.portLayer = L.layerGroup();
  }

  _bindEvents() {
    // Toggle panel
    document.getElementById('admin-toggle').addEventListener('click', () => {
      const panel = document.getElementById('admin-panel');
      panel.classList.toggle('collapsed');
      document.getElementById('admin-toggle').textContent =
        panel.classList.contains('collapsed') ? '▼' : '▲';
    });

    // Mode buttons
    document.querySelectorAll('[data-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.setMode(btn.dataset.mode);
        document.querySelectorAll('[data-mode]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });

    // Click to add waypoint in edit mode (when a waypoint is selected, connect to it)
    // Double-click when nothing selected to add standalone waypoint
    // Shift+drag for box-select & merge
    let clickStart = null;
    let clickMoved = false;
    let boxSelectActive = false;
    let boxSelectRect = null;

    this.map.on('mousedown', (e) => {
      if (!this.visible || this.mode !== 'edit') return;
      clickStart = e.latlng;
      clickMoved = false;

      // Shift+drag: start box selection
      if (e.originalEvent.shiftKey) {
        boxSelectActive = true;
        this.map.dragging.disable();
        boxSelectRect = L.rectangle([clickStart, clickStart], {
          color: '#e040fb', weight: 2, fillColor: '#e040fb', fillOpacity: 0.15,
          dashArray: '6,4',
          pane: 'adminWaypointPane'
        }).addTo(this.waypointLayer);
      }
    });

    this.map.on('mousemove', (e) => {
      if (clickStart) clickMoved = true;

      if (boxSelectActive && boxSelectRect && clickStart) {
        boxSelectRect.setBounds(L.latLngBounds(clickStart, e.latlng));
        // Highlight waypoints inside the box (check all world copies)
        const bounds = boxSelectRect.getBounds();
        for (const wp of this.waypoints) {
          const inside = [-360, 0, 360].some(off => bounds.contains([wp.lat, wp.lng + off]));
          this._setWaypointStyle(wp.id, {
            color: inside ? '#e040fb' : '#ff9800',
            fillColor: inside ? '#e040fb' : '#ff9800'
          });
        }
      }
    });

    this.map.on('mouseup', (e) => {
      if (!this.visible || this.mode !== 'edit' || !clickStart) return;

      // Box selection complete
      if (boxSelectActive) {
        boxSelectActive = false;
        this.map.dragging.enable();
        const bounds = boxSelectRect ? boxSelectRect.getBounds() : null;
        if (boxSelectRect) {
          boxSelectRect.remove();
          boxSelectRect = null;
        }

        if (bounds && clickMoved) {
          // Find all waypoints inside bounds (check all world copies)
          const selected = this.waypoints.filter(wp =>
            [-360, 0, 360].some(off => bounds.contains([wp.lat, wp.lng + off]))
          );
          if (selected.length >= 2) {
            this._mergeMultipleWaypoints(selected);
          } else {
            this._updateWaypointStyles();
          }
        }
        clickStart = null;
        clickMoved = false;
        return;
      }

      // Only trigger if it was a click (not a drag)
      if (!clickMoved) {
        // Normalize longitude to [-180, 180]
        let lng = e.latlng.lng;
        while (lng > 180) lng -= 360;
        while (lng < -180) lng += 360;

        if (this.selectedWaypoint) {
          // Create new waypoint connected to selected
          this._addConnectedWaypoint(e.latlng.lat, lng);
        }
        // If nothing selected, double-click still works for standalone waypoint
      }

      clickStart = null;
      clickMoved = false;
    });

    this.map.on('dblclick', (e) => {
      if (this.visible && this.mode === 'edit' && !this.selectedWaypoint) {
        L.DomEvent.stopPropagation(e);
        let lng = e.latlng.lng;
        while (lng > 180) lng -= 360;
        while (lng < -180) lng += 360;
        this._addWaypoint(e.latlng.lat, lng);
      }
    });

    // Export
    document.getElementById('admin-export').addEventListener('click', () => this._exportJSON());

    // Reload
    document.getElementById('admin-reload').addEventListener('click', () => this._loadWaypoints());

    // Clear test
    document.getElementById('admin-clear-test').addEventListener('click', () => this._clearTestRoute());

    // Port selection for test mode
    EventBus.on('ui:portSelected', ({ port }) => {
      if (this.visible && this.mode === 'test_route') {
        this._handleTestRoutePortClick(port);
      }
    });

    // Prevent browser context menu when admin panel is visible
    this.map.getContainer().addEventListener('contextmenu', (e) => {
      if (this.visible) {
        e.preventDefault();
      }
    });

    // Ctrl+Z for undo
    document.addEventListener('keydown', (e) => {
      if (this.visible && e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        this._undo();
      }
    });

    // Save button
    document.getElementById('admin-save').addEventListener('click', () => this._saveToServer());

    // Undo button
    document.getElementById('admin-undo').addEventListener('click', () => this._undo());

    // Save ports button
    document.getElementById('admin-save-ports').addEventListener('click', () => this._savePortsToServer());

    // Add port button — opens the editor with a blank draft centered on
    // the current map view; the new port can then be dragged into place
    // like any other (see _makePortDraggable()).
    document.getElementById('admin-add-port').addEventListener('click', () => this._openPortEditor(null));

    // Port picker "Edit" button — reaches a port by name/id regardless of
    // whatever else happens to be sitting on top of its map marker (see
    // _renderPortPicker()'s docblock).
    document.getElementById('admin-edit-port').addEventListener('click', () => {
      const id = document.getElementById('admin-port-picker').value;
      const port = this.ports.find(p => p.id === id);
      if (port) this._openPortEditor(port);
    });
  }

  show() {
    this.visible = true;
    document.getElementById('admin-panel').style.display = 'block';
    this.waypointLayer.addTo(this.map);
    this.connectionLayer.addTo(this.map);
    this.testRouteLayer.addTo(this.map);
    this.portLayer.addTo(this.map);
    this._loadWaypoints();
    this._loadPorts();
  }

  hide() {
    this.visible = false;
    document.getElementById('admin-panel').style.display = 'none';
    this.map.removeLayer(this.waypointLayer);
    this.map.removeLayer(this.connectionLayer);
    this.map.removeLayer(this.testRouteLayer);
    this.map.removeLayer(this.portLayer);
    this._clearTestRoute();
  }

  toggle() {
    if (this.visible) this.hide();
    else this.show();
  }

  setMode(mode) {
    this.mode = mode;
    this.selectedWaypoint = null;
    this.connectStart = null;
    this.testRouteStart = null;

    // Show relevant help
    document.getElementById('help-edit').style.display = mode === 'edit' ? 'block' : 'none';
    document.getElementById('help-connect').style.display = mode === 'connect' ? 'block' : 'none';
    document.getElementById('help-test').style.display = mode === 'test_route' ? 'block' : 'none';

    // Update cursor
    const mapContainer = this.map.getContainer();
    mapContainer.style.cursor = mode === 'edit' ? 'default' : '';

    if (mode === 'test_route') {
      document.getElementById('admin-route-info').textContent = 'Click first port...';
    } else if (mode === 'connect') {
      document.getElementById('admin-route-info').textContent = 'Click first waypoint to connect...';
    } else if (mode === 'edit') {
      document.getElementById('admin-route-info').textContent = 'Double-click to add waypoint, or click existing to select';
    }

    // Reset waypoint styles
    this._updateWaypointStyles();
  }

  async _loadWaypoints() {
    try {
      const data = await fetch('/data/seaWaypoints.json').then(r => r.json());
      this.waypoints = data.waypoints || [];
      this.connections = data.connections || data.edges || [];
      // Normalize all longitudes to [-180, 180] for consistent world-copy handling
      for (const wp of this.waypoints) {
        while (wp.lng > 180) wp.lng -= 360;
        while (wp.lng < -180) wp.lng += 360;
      }
      this._forgetRemovedWaypoints();
      this._render();
    } catch (e) {
      console.error('Failed to load waypoints:', e);
    }
  }

  /**
   * Fetch the raw ports.json content fresh — deliberately NOT read from
   * GameState.ports (real Port entities, richer/derived shape) so that
   * dragging a port here only ever mutates the exact .lat/.lng fields on
   * these plain rows; every other field round-trips byte-for-byte back to
   * the server on save with nothing reconstructed or guessed at.
   */
  async _loadPorts() {
    try {
      const data = await fetch('/data/ports.json').then(r => r.json());
      this.ports = data.ports || [];
      this._renderPorts();
      this._updatePortStats();
    } catch (e) {
      console.error('Failed to load ports:', e);
    }
  }

  _render() {
    this._renderConnections(); // Render connections first (below waypoints)
    this._renderWaypoints();
    this._updateStats();
  }

  _renderWaypoints() {
    this.waypointLayer.clearLayers();
    this.waypointMarkers.clear();

    // Render waypoints on multiple world copies for continuous display
    const worldOffsets = [-360, 0, 360];

    for (const wp of this.waypoints) {
      const markers = [];

      for (const offset of worldOffsets) {
        const marker = L.circleMarker([wp.lat, wp.lng + offset], {
          radius: 8,
          color: '#ff9800',
          fillColor: '#ff9800',
          fillOpacity: 0.9,
          weight: 2,
          className: 'waypoint-marker',
          pane: 'adminWaypointPane'
        }).addTo(this.waypointLayer);

        marker.bindTooltip(wp.id, { permanent: false, direction: 'top', offset: [0, -10] });

        // All world copies are draggable (offset subtracted on save)
        this._makeWaypointDraggable(marker, wp, offset);

        // All copies are clickable
        marker.on('contextmenu', (e) => {
          L.DomEvent.stopPropagation(e);
          L.DomEvent.preventDefault(e);
          this._handleWaypointRightClick(wp);
        });

        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          if (this.mode === 'connect') {
            this._handleConnectClick(wp);
          } else if (this.mode === 'edit') {
            // Toggle selection or select new waypoint
            if (this.selectedWaypoint && this.selectedWaypoint.id === wp.id) {
              this._deselectWaypoint();
            } else {
              this._selectWaypoint(wp);
            }
          }
        });

        markers.push(marker);
      }

      this.waypointMarkers.set(wp.id, markers); // Store all world-copy markers
    }
  }

  /** Render every port as a translucent red ring — reference layer, draggable to correct its position. */
  _renderPorts() {
    this.portLayer.clearLayers();
    this.portMarkers.clear();

    const worldOffsets = [-360, 0, 360];

    for (const port of this.ports) {
      const markers = [];

      for (const offset of worldOffsets) {
        const marker = L.circleMarker([port.lat, port.lng + offset], {
          radius: 12,
          color: '#e74c3c',
          weight: 2,
          fillColor: '#e74c3c',
          fillOpacity: 0.12,
          className: 'admin-port-marker',
          pane: 'adminPortPane'
        }).addTo(this.portLayer);

        marker.bindTooltip(port.name, { permanent: false, direction: 'top', offset: [0, -12] });

        this._makePortDraggable(marker, port, offset);

        markers.push(marker);
      }

      this.portMarkers.set(port.id, markers);
    }
  }

  /**
   * Handles BOTH dragging a port to reposition it AND clicking it to open
   * the field editor / (in test_route mode) select it for a route test —
   * both start with the same mousedown, so click-vs-drag has to be decided
   * within this one mousedown/mousemove/mouseup flow, the same way
   * _bindEvents()'s waypoint click/drag/box-select handling already does
   * (its `clickMoved` flag). A separate marker.on('click', ...) handler
   * does NOT work here: onUp() below always used to call _renderPorts(),
   * which clears and rebuilds every port marker — by the time the browser
   * got around to firing its own synthetic 'click' event, the original
   * marker element was already gone, so the click silently never fired at
   * all. (Confirmed live: a real click never opened the editor, only the
   * drag/reposition path ever ran.)
   */
  _makePortDraggable(marker, port, worldOffset = 0) {
    marker.on('mousedown', (e) => {
      if (e.originalEvent.button !== 0) return; // Left click only
      L.DomEvent.stopPropagation(e);

      this.map.dragging.disable();
      let moved = false;

      const onMove = (moveEvent) => {
        if (!moved) {
          // First real movement is what makes this a drag rather than a
          // click — only NOW do we touch undo/unsaved state, so a plain
          // click never pushes a no-op undo step or lights up "unsaved
          // changes" for a position that didn't actually change.
          moved = true;
          this._saveState('ports');
          marker.setStyle({ color: '#2ecc71', fillColor: '#2ecc71', fillOpacity: 0.35 });
        }
        marker.setLatLng(moveEvent.latlng);
      };

      const onUp = () => {
        this.map.dragging.enable();
        this.map.off('mousemove', onMove);
        this.map.off('mouseup', onUp);

        if (!moved) {
          // A plain click, not a drag.
          if (this.mode === 'test_route') {
            this._handleTestRoutePortClick(GameState.getPort(port.id) || port);
          } else {
            this._openPortEditor(port);
          }
          return;
        }

        port.lat = marker.getLatLng().lat;
        let newLng = marker.getLatLng().lng - worldOffset;
        while (newLng > 180) newLng -= 360;
        while (newLng < -180) newLng += 360;
        port.lng = newLng;

        // Resync every world-copy marker (including this one) to the
        // canonical position, same as _makeWaypointDraggable does.
        this._renderPorts();
      };

      this.map.on('mousemove', onMove);
      this.map.on('mouseup', onUp);
    });
  }

  /** "New York Harbor" -> "new_york_harbor" — a starting suggestion only, never auto-applied over a value the user already typed. */
  _slugifyPortId(name) {
    return String(name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Full field editor for one port — add, edit, or remove. Operates on a
   * DRAFT copy so Cancel is a true no-op; only Save mutates the real
   * `this.ports` row (or pushes a new one) and re-renders. Every field on
   * a ports.json row is editable here (see the module docblock's field
   * list in _loadPorts()) — lat/lng included, so this doubles as a
   * precise alternative to dragging the ring.
   *
   * @param {Object|null} port  An existing row from `this.ports`, or null to add a new one.
   */
  _openPortEditor(port) {
    const isNew = !port;
    const draft = isNew
      ? {
          id: '', name: '', country: '', region: PORT_REGIONS[0],
          lat: Math.round(this.map.getCenter().lat * 1000) / 1000,
          lng: Math.round(this.map.getCenter().lng * 1000) / 1000,
          size: 'small', maxShipSize: 'large_cargo', berthCount: 4,
          hasFuel: true, hasRepair: false, hasShipyard: false, hasWarehouse: false,
          hasBulkLiquidTerminal: false, hasBulkDryTerminal: false, hasContainerTerminal: false,
          hasRefrigeratedTerminal: false, hasRoRoTerminal: false,
          wealthLevel: 5, industrialLevel: 5, populationLevel: 5,
          producedGoods: [], demandedGoods: [],
          baseFuelPrice: 500, dockingFee: 5000, loadingFee: 50
        }
      : { ...port, producedGoods: [...(port.producedGoods || [])], demandedGoods: [...(port.demandedGoods || [])] };

    const modal = new Modal(`port-editor-${Date.now()}`, { title: isNew ? 'Add Port' : `Edit Port: ${port.name}` });
    const cleanup = () => { setTimeout(() => { if (modal.el?.parentNode) modal.el.remove(); }, 200); };
    // ~20 fields + two cargo checklists need more room than the default
    // modal width.
    const box = modal.el.querySelector('.modal-box');
    if (box) { box.style.width = '760px'; box.style.maxWidth = '92vw'; }

    const numberField = (label, field, value, opts = '') =>
      `<div class="port-field"><label>${label}</label><input type="number" data-field="${field}" value="${value}" ${opts}></div>`;
    const textField = (label, field, value, opts = '') =>
      `<div class="port-field"><label>${label}</label><input type="text" data-field="${field}" value="${escapeHtml(value)}" ${opts}></div>`;
    const selectField = (label, field, options, selected) =>
      `<div class="port-field"><label>${label}</label><select data-field="${field}">
        ${options.map(o => `<option value="${o}" ${o === selected ? 'selected' : ''}>${o}</option>`).join('')}
      </select></div>`;
    const checkbox = (label, field, checked) =>
      `<label class="port-field-checkbox"><input type="checkbox" data-field="${field}" ${checked ? 'checked' : ''}> ${label}</label>`;

    modal.setBody(`
      <div class="port-editor">
        <div class="port-field-grid">
          ${textField('Port ID', 'id', draft.id)}
          ${textField('Name', 'name', draft.name)}
          ${textField('Country', 'country', draft.country)}
          ${selectField('Region', 'region', PORT_REGIONS, draft.region)}
          ${numberField('Latitude', 'lat', draft.lat, 'step="0.0001" min="-90" max="90"')}
          ${numberField('Longitude', 'lng', draft.lng, 'step="0.0001" min="-180" max="180"')}
          ${selectField('Size', 'size', PORT_SIZES, draft.size)}
          ${textField('Max ship type (id)', 'maxShipSize', draft.maxShipSize)}
          ${numberField('Berth count', 'berthCount', draft.berthCount, 'min="1"')}
          ${numberField('Wealth level', 'wealthLevel', draft.wealthLevel, 'min="1" max="10"')}
          ${numberField('Industrial level', 'industrialLevel', draft.industrialLevel, 'min="1" max="10"')}
          ${numberField('Population level', 'populationLevel', draft.populationLevel, 'min="1" max="10"')}
          ${numberField('Base fuel price', 'baseFuelPrice', draft.baseFuelPrice, 'min="0"')}
          ${numberField('Docking fee', 'dockingFee', draft.dockingFee, 'min="0"')}
          ${numberField('Loading fee / TEU', 'loadingFee', draft.loadingFee, 'min="0"')}
        </div>

        <div class="port-field-services">
          <label>Services</label>
          ${checkbox('Fuel', 'hasFuel', draft.hasFuel)}
          ${checkbox('Repair', 'hasRepair', draft.hasRepair)}
          ${checkbox('Shipyard', 'hasShipyard', draft.hasShipyard)}
          ${checkbox('Warehouse', 'hasWarehouse', draft.hasWarehouse)}
        </div>

        <div class="port-field-services">
          <label>Cargo Terminals (hard gate — produced/demanded goods below must stay within these)</label>
          ${PORT_TERMINALS.map(t => checkbox(t.label, t.field, draft[t.field])).join('')}
        </div>

        <div class="port-field-cargo">
          <label>Produced goods</label>
          ${this._cargoChecklistHtml('producedGoods', draft.producedGoods)}
        </div>
        <div class="port-field-cargo">
          <label>Demanded goods</label>
          ${this._cargoChecklistHtml('demandedGoods', draft.demandedGoods)}
        </div>

        <div id="port-editor-error" class="port-editor-error" style="display:none"></div>

        <div class="menu-buttons" style="margin-top:1rem">
          ${!isNew ? '<button class="btn btn-sm btn-danger" id="port-editor-delete">Delete Port</button>' : ''}
          <button class="btn btn-sm" id="port-editor-cancel">Cancel</button>
          <button class="btn btn-sm btn-primary" id="port-editor-save">${isNew ? 'Add Port' : 'Save'}</button>
        </div>
      </div>
    `);

    const showError = (msg) => {
      const el = modal.el.querySelector('#port-editor-error');
      el.textContent = msg;
      el.style.display = 'block';
    };

    if (isNew) {
      // Suggest an id from the name as it's typed, but only while the id
      // field is still untouched — never overwrite something the user
      // deliberately typed themselves.
      const idInput = modal.el.querySelector('[data-field="id"]');
      modal.el.querySelector('[data-field="name"]').addEventListener('input', (e) => {
        if (!idInput.value || idInput.dataset.autoFilled === 'true') {
          idInput.value = this._slugifyPortId(e.target.value);
          idInput.dataset.autoFilled = 'true';
        }
      });
      idInput.addEventListener('input', () => { idInput.dataset.autoFilled = 'false'; });
    }

    modal.el.querySelector('#port-editor-cancel').addEventListener('click', () => { modal.close(); cleanup(); });

    if (!isNew) {
      modal.el.querySelector('#port-editor-delete').addEventListener('click', async () => {
        const ok = await showConfirm(`Permanently remove ${port.name} from ports.json? This cannot be undone by Cancel — only by Undo (Ctrl+Z) before you Save Ports.`, { title: 'Delete Port', confirmLabel: 'Delete' });
        if (!ok) return;
        this._saveState('ports');
        this.ports = this.ports.filter(p => p.id !== port.id);
        this._renderPorts();
        this._updatePortStats();
        modal.close();
        cleanup();
      });
    }

    modal.el.querySelector('#port-editor-save').addEventListener('click', () => {
      const root = modal.el;
      const val = (field) => root.querySelector(`[data-field="${field}"]`).value;
      const checked = (field) => root.querySelector(`[data-field="${field}"]`).checked;
      const checkedList = (field) => [...root.querySelectorAll(`[data-field="${field}"]:checked`)].map(el => el.value);

      const id = val('id').trim();
      const name = val('name').trim();
      if (!id || !name) return showError('Port ID and Name are required.');
      if (isNew && this.ports.some(p => p.id === id)) return showError(`A port with id "${id}" already exists.`);
      if (!isNew && id !== port.id && this.ports.some(p => p.id === id)) return showError(`A port with id "${id}" already exists.`);

      const lat = Number(val('lat')), lng = Number(val('lng'));
      if (!Number.isFinite(lat) || lat < -90 || lat > 90) return showError('Latitude must be between -90 and 90.');
      if (!Number.isFinite(lng) || lng < -180 || lng > 180) return showError('Longitude must be between -180 and 180.');

      const producedGoods = checkedList('producedGoods');
      const demandedGoods = checkedList('demandedGoods');
      const terminals = {};
      for (const t of PORT_TERMINALS) terminals[t.field] = checked(t.field);

      // Hard gate: a port can't produce/demand a cargo whose category it
      // has no terminal for (src/entities/Port.js's canHandleCargo() would
      // never let it actually trade — better to catch the mismatch here
      // than ship data TradeSystem.js silently can't honor).
      const enabledCategories = new Set(PORT_TERMINALS.filter(t => terminals[t.field]).map(t => t.category));
      const conflicts = [];
      for (const cargoId of [...producedGoods, ...demandedGoods]) {
        const category = CARGO_TYPES[cargoId]?.category;
        if (category && !enabledCategories.has(category)) conflicts.push(`${CARGO_TYPES[cargoId].name} (${category})`);
      }
      if (conflicts.length > 0) {
        return showError(`These goods need a terminal that isn't checked: ${[...new Set(conflicts)].join(', ')}. Either uncheck the good or enable its terminal.`);
      }

      const updated = {
        id, name,
        country: val('country').trim(),
        region: val('region'),
        lat, lng,
        size: val('size'),
        maxShipSize: val('maxShipSize').trim(),
        berthCount: Number(val('berthCount')) || 1,
        hasFuel: checked('hasFuel'), hasRepair: checked('hasRepair'),
        hasShipyard: checked('hasShipyard'), hasWarehouse: checked('hasWarehouse'),
        ...terminals,
        wealthLevel: Number(val('wealthLevel')) || 5,
        industrialLevel: Number(val('industrialLevel')) || 5,
        populationLevel: Number(val('populationLevel')) || 5,
        producedGoods,
        demandedGoods,
        baseFuelPrice: Number(val('baseFuelPrice')) || 0,
        dockingFee: Number(val('dockingFee')) || 0,
        loadingFee: Number(val('loadingFee')) || 0
      };

      this._saveState('ports');
      if (isNew) {
        this.ports.push(updated);
      } else {
        Object.assign(port, updated);
      }
      this._renderPorts();
      this._updatePortStats();
      modal.close();
      cleanup();
      EventBus.emit('ui:notification', {
        message: `${isNew ? 'Added' : 'Updated'} ${updated.name} — click Save Ports to persist.`,
        type: 'success'
      });
    });

    modal.open();
  }

  /** Checkbox grid for one produced/demanded goods field, grouped by cargo category. */
  _cargoChecklistHtml(fieldName, selectedIds) {
    const byCategory = {};
    for (const cargo of Object.values(CARGO_TYPES)) {
      (byCategory[cargo.category] = byCategory[cargo.category] || []).push(cargo);
    }
    return Object.entries(byCategory).map(([category, cargos]) => `
      <div class="cargo-category-group">
        <span class="cargo-category-label">${category.replace(/_/g, ' ')}</span>
        <div class="cargo-checklist">
          ${cargos.map(c => `
            <label class="cargo-checklist-item">
              <input type="checkbox" data-field="${fieldName}" value="${c.id}" ${selectedIds.includes(c.id) ? 'checked' : ''}>
              ${c.name}
            </label>`).join('')}
        </div>
      </div>`).join('');
  }

  _findMergeTarget(draggedWp, latlng) {
    const MERGE_PIXELS = 20;
    let closest = null;
    let closestDist = Infinity;
    const pt1 = this.map.latLngToContainerPoint(latlng);

    for (const wp of this.waypoints) {
      if (wp.id === draggedWp.id) continue;
      // Check all world copies of this waypoint for screen proximity
      for (const offset of [-360, 0, 360]) {
        const pt2 = this.map.latLngToContainerPoint([wp.lat, wp.lng + offset]);
        const dx = pt1.x - pt2.x;
        const dy = pt1.y - pt2.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < MERGE_PIXELS && dist < closestDist) {
          closestDist = dist;
          closest = wp;
        }
      }
    }
    return closest;
  }

  _mergeWaypoints(sourceWp, targetWp) {
    // Transfer all connections from source to target
    const newConnections = [];
    for (const [a, b] of this.connections) {
      let na = a === sourceWp.id ? targetWp.id : a;
      let nb = b === sourceWp.id ? targetWp.id : b;
      // Skip self-loops and duplicates
      if (na === nb) continue;
      if (!newConnections.some(([x, y]) => (x === na && y === nb) || (x === nb && y === na))) {
        newConnections.push([na, nb]);
      }
    }
    this.connections = newConnections;

    // Remove source waypoint
    this.waypoints = this.waypoints.filter(w => w.id !== sourceWp.id);

    this._forgetRemovedWaypoints();
    this._render();
    EventBus.emit('ui:notification', {
      message: `Merged ${sourceWp.id} into ${targetWp.id}`,
      type: 'success'
    });
  }

  _mergeMultipleWaypoints(selected) {
    if (selected.length < 2) return;

    this._saveState();

    // Calculate center of all selected waypoints
    const centerLat = selected.reduce((s, w) => s + w.lat, 0) / selected.length;
    const centerLng = selected.reduce((s, w) => s + w.lng, 0) / selected.length;

    // Keep the first waypoint as the survivor, move it to center
    const survivor = selected[0];
    const removedIds = new Set(selected.slice(1).map(w => w.id));

    survivor.lat = centerLat;
    survivor.lng = centerLng;

    // Rewrite all connections: remap removed IDs to survivor
    const newConnections = [];
    for (const [a, b] of this.connections) {
      const na = removedIds.has(a) ? survivor.id : a;
      const nb = removedIds.has(b) ? survivor.id : b;
      if (na === nb) continue; // skip self-loops
      if (!newConnections.some(([x, y]) => (x === na && y === nb) || (x === nb && y === na))) {
        newConnections.push([na, nb]);
      }
    }
    this.connections = newConnections;

    // Remove merged waypoints
    this.waypoints = this.waypoints.filter(w => !removedIds.has(w.id));

    this._forgetRemovedWaypoints();
    this._render();
    EventBus.emit('ui:notification', {
      message: `Merged ${selected.length} waypoints into one`,
      type: 'success'
    });
  }

  _makeWaypointDraggable(marker, wp, worldOffset = 0) {
    let isDragging = false;
    let mergeTarget = null;

    marker.on('mousedown', (e) => {
      if (this.mode !== 'edit') return;
      if (e.originalEvent.button !== 0) return; // Left click only
      if (e.originalEvent.shiftKey) return; // Don't drag during box-select

      L.DomEvent.stopPropagation(e);
      this._saveState();

      isDragging = true;
      mergeTarget = null;
      this.map.dragging.disable();

      marker.setStyle({ color: '#00ff00', fillColor: '#00ff00' });

      const onMove = (moveEvent) => {
        marker.setLatLng(moveEvent.latlng);

        // Check for merge target (compare screen positions of nearby markers)
        const prev = mergeTarget;
        mergeTarget = this._findMergeTarget(wp, moveEvent.latlng);

        // Reset previous target style
        if (prev && prev !== mergeTarget) {
          this._setWaypointStyle(prev.id, { color: '#ff9800', fillColor: '#ff9800' });
        }

        if (mergeTarget) {
          // Highlight both in magenta
          marker.setStyle({ color: '#e040fb', fillColor: '#e040fb' });
          this._setWaypointStyle(mergeTarget.id, { color: '#e040fb', fillColor: '#e040fb' });
        } else {
          marker.setStyle({ color: '#00ff00', fillColor: '#00ff00' });
        }
      };

      const onUp = () => {
        isDragging = false;
        this.map.dragging.enable();
        this.map.off('mousemove', onMove);
        this.map.off('mouseup', onUp);

        if (mergeTarget) {
          this._mergeWaypoints(wp, mergeTarget);
        } else {
          // Update waypoint position, subtracting world offset to get canonical lng
          wp.lat = marker.getLatLng().lat;
          let newLng = marker.getLatLng().lng - worldOffset;
          while (newLng > 180) newLng -= 360;
          while (newLng < -180) newLng += 360;
          wp.lng = newLng;

          this._renderConnections();
          marker.setStyle({ color: '#ff9800', fillColor: '#ff9800' });
        }
      };

      this.map.on('mousemove', onMove);
      this.map.on('mouseup', onUp);
    });
  }

  _renderConnections() {
    this.connectionLayer.clearLayers();
    this.connectionLines.clear();

    // Render connections on multiple world copies
    const worldOffsets = [-360, 0, 360];

    for (const [aId, bId] of this.connections) {
      const a = this.waypoints.find(w => w.id === aId);
      const b = this.waypoints.find(w => w.id === bId);
      if (!a || !b) continue;

      // Normalize for dateline crossing
      const normalized = normalizePolylineForDateline([a, b]);

      for (const offset of worldOffsets) {
        const latlngs = normalized.map(w => [w.lat, w.lng + offset]);

        const line = L.polyline(latlngs, {
          color: '#ff9800',
          weight: 3,
          opacity: 0.6,
          pane: 'adminConnectionPane'
        }).addTo(this.connectionLayer);

        // Only make the primary (offset=0) line interactive
        if (offset === 0) {
          const key = this._connectionKey(aId, bId);
          this.connectionLines.set(key, { line, wp1: a, wp2: b });
          this._makeLineDraggable(line, a, b);
        } else {
          // Ghost copies - still allow drag to insert
          this._makeLineDraggable(line, a, b);
        }
      }
    }
  }

  _makeLineDraggable(line, wp1, wp2) {
    let ghostMarker = null;
    let isDragging = false;

    line.on('mousedown', (e) => {
      if (this.mode !== 'edit') return;
      if (e.originalEvent.button !== 0) return;

      L.DomEvent.stopPropagation(e);
      isDragging = true;
      this.map.dragging.disable();

      // Create ghost marker at click position
      ghostMarker = L.circleMarker(e.latlng, {
        radius: 8,
        color: '#00ff00',
        fillColor: '#00ff00',
        fillOpacity: 0.9,
        weight: 2,
        pane: 'adminWaypointPane'
      }).addTo(this.waypointLayer);

      const onMove = (moveEvent) => {
        if (ghostMarker) {
          ghostMarker.setLatLng(moveEvent.latlng);
        }
      };

      const onUp = (upEvent) => {
        isDragging = false;
        this.map.dragging.enable();
        this.map.off('mousemove', onMove);
        this.map.off('mouseup', onUp);

        if (ghostMarker) {
          const pos = ghostMarker.getLatLng();
          ghostMarker.remove();
          ghostMarker = null;

          // Insert new waypoint between wp1 and wp2
          this._insertWaypointOnLine(wp1, wp2, pos.lat, pos.lng);
        }
      };

      this.map.on('mousemove', onMove);
      this.map.on('mouseup', onUp);
    });
  }

  _insertWaypointOnLine(wp1, wp2, lat, lng) {
    this._saveState();

    // Create new waypoint
    const newId = `wp_${Date.now()}`;
    const newWp = { id: newId, lat, lng };
    this.waypoints.push(newWp);

    // Remove old connection
    this._removeConnection(wp1.id, wp2.id);

    // Add two new connections
    this.connections.push([wp1.id, newId]);
    this.connections.push([newId, wp2.id]);

    this._render();
  }

  _handleWaypointRightClick(wp) {
    this._saveState();

    // Find all connections involving this waypoint
    const connectedIds = [];
    for (const [aId, bId] of this.connections) {
      if (aId === wp.id) connectedIds.push(bId);
      else if (bId === wp.id) connectedIds.push(aId);
    }

    if (connectedIds.length === 2) {
      // Remove waypoint and connect the two neighbors
      const [neighbor1, neighbor2] = connectedIds;

      // Remove connections to this waypoint
      this.connections = this.connections.filter(
        ([a, b]) => a !== wp.id && b !== wp.id
      );

      // Add connection between neighbors (if not already exists)
      if (!this._connectionExists(neighbor1, neighbor2)) {
        this.connections.push([neighbor1, neighbor2]);
      }

      // Remove waypoint
      this.waypoints = this.waypoints.filter(w => w.id !== wp.id);
    } else {
      // Just remove the waypoint and all its connections
      this.connections = this.connections.filter(
        ([a, b]) => a !== wp.id && b !== wp.id
      );
      this.waypoints = this.waypoints.filter(w => w.id !== wp.id);
    }

    this._forgetRemovedWaypoints();
    this._render();
  }

  _handleConnectClick(wp) {
    if (!this.connectStart) {
      this.connectStart = wp;
      this._setWaypointStyle(wp.id, { color: '#00ff00', fillColor: '#00ff00' });
      document.getElementById('admin-route-info').textContent = `Connect from ${wp.id}. Click second waypoint...`;
    } else {
      if (this.connectStart.id !== wp.id && this._waypointExists(this.connectStart.id)) {
        // Create connection
        if (!this._connectionExists(this.connectStart.id, wp.id)) {
          this._saveState();
          this.connections.push([this.connectStart.id, wp.id]);
          this._render();
        }
      }
      this._updateWaypointStyles();
      this.connectStart = null;
      document.getElementById('admin-route-info').textContent = 'Click first waypoint to connect...';
    }
  }

  _addWaypoint(lat, lng) {
    this._saveState();

    const newId = `wp_${Date.now()}`;
    const newWp = { id: newId, lat, lng };
    this.waypoints.push(newWp);
    this._render();
    this._selectWaypoint(newWp);
  }

  _addConnectedWaypoint(lat, lng) {
    if (!this.selectedWaypoint) return;
    if (!this._waypointExists(this.selectedWaypoint.id)) {
      this._forgetRemovedWaypoints();   // stale selection — don't link to a removed waypoint
      return;
    }

    this._saveState();

    const newId = `wp_${Date.now()}`;
    const newWp = { id: newId, lat, lng };
    this.waypoints.push(newWp);

    // Connect to previously selected waypoint
    this.connections.push([this.selectedWaypoint.id, newId]);

    this._render();
    this._selectWaypoint(newWp); // Select the new waypoint for chaining
  }

  _selectWaypoint(wp) {
    this.selectedWaypoint = wp;
    this._updateWaypointStyles();
    document.getElementById('admin-route-info').textContent = wp
      ? `Selected: ${wp.id} (click map to extend route)`
      : 'No waypoint selected';
  }

  _deselectWaypoint() {
    this.selectedWaypoint = null;
    this._updateWaypointStyles();
    document.getElementById('admin-route-info').textContent = 'Double-click to add waypoint';
  }

  /**
   * Drop the selection / pending connect start if their waypoint no longer
   * exists (deleted, merged away, or replaced by a reload). Without this the
   * next map click chained a new waypoint onto the removed id — saved as an
   * edge to nothing, leaving the new point unconnected. A port whose nearest
   * waypoint was such an orphan routed in a straight line.
   */
  _forgetRemovedWaypoints() {
    const byId = new Map(this.waypoints.map(w => [w.id, w]));
    if (this.selectedWaypoint) {
      const current = byId.get(this.selectedWaypoint.id);
      if (current) this.selectedWaypoint = current;
      else this._deselectWaypoint();
    }
    if (this.connectStart && !byId.has(this.connectStart.id)) {
      this.connectStart = null;
    }
  }

  _waypointExists(id) {
    return this.waypoints.some(w => w.id === id);
  }

  /** Set style on all world-copy markers for a waypoint */
  _setWaypointStyle(wpId, style) {
    const markers = this.waypointMarkers.get(wpId);
    if (markers) markers.forEach(m => m.setStyle(style));
  }

  _updateWaypointStyles() {
    // Update all waypoint markers to show selection state
    for (const wp of this.waypoints) {
      const isSelected = this.selectedWaypoint && this.selectedWaypoint.id === wp.id;
      this._setWaypointStyle(wp.id, {
        color: isSelected ? '#00ff00' : '#ff9800',
        fillColor: isSelected ? '#00ff00' : '#ff9800'
      });
    }
  }

  _removeConnection(id1, id2) {
    this.connections = this.connections.filter(
      ([a, b]) => !((a === id1 && b === id2) || (a === id2 && b === id1))
    );
  }

  _connectionExists(id1, id2) {
    return this.connections.some(
      ([a, b]) => (a === id1 && b === id2) || (a === id2 && b === id1)
    );
  }

  _connectionKey(id1, id2) {
    return [id1, id2].sort().join('|');
  }

  _handleTestRoutePortClick(port) {
    if (!this.testRouteStart) {
      this.testRouteStart = port;
      document.getElementById('admin-route-info').textContent = `From: ${port.name}. Click destination...`;
    } else {
      this._testRoute(this.testRouteStart, port);
      this.testRouteStart = null;
    }
  }

  _testRoute(startPort, endPort) {
    // Reinitialize routes with current waypoint data
    initSeaRoutes({ waypoints: this.waypoints, connections: this.connections });

    const route = calculateRoute(startPort.position, endPort.position);

    // Draw test route - normalize for dateline crossing
    this._clearTestRoute();
    const normalized = normalizePolylineForDateline(route.waypoints);
    const latlngs = normalized.map(w => [w.lat, w.lng]);
    this.testRouteLine = L.polyline(latlngs, {
      color: '#00ff00',
      weight: 4,
      opacity: 0.9
    }).addTo(this.testRouteLayer);

    // Show waypoint markers on route
    normalized.forEach((w, i) => {
      if (i > 0 && i < normalized.length - 1) {
        L.circleMarker([w.lat, w.lng], {
          radius: 4,
          color: '#00ff00',
          fillColor: '#00ff00',
          fillOpacity: 1
        }).addTo(this.testRouteLayer);
      }
    });

    document.getElementById('admin-route-info').innerHTML = `
      <b>${startPort.name} → ${endPort.name}</b><br>
      Distance: ${Math.round(route.distance)} nm<br>
      Waypoints: ${route.waypoints.length - 2} intermediate
    `;
  }

  _clearTestRoute() {
    this.testRouteLayer.clearLayers();
    this.testRouteLine = null;
    this.testRouteStart = null;
    if (this.mode === 'test_route') {
      document.getElementById('admin-route-info').textContent = 'Click first port...';
    }
  }

  _updateStats() {
    document.getElementById('admin-stats').textContent =
      `${this.waypoints.length} waypoints, ${this.connections.length} connections`;
  }

  _exportJSON() {
    const data = {
      waypoints: this.waypoints.map(w => ({
        id: w.id,
        lat: Math.round(w.lat * 1000) / 1000,
        lng: Math.round(w.lng * 1000) / 1000
      })),
      connections: this.connections
    };
    const json = JSON.stringify(data, null, 2);

    navigator.clipboard.writeText(json).then(() => {
      EventBus.emit('ui:notification', {
        message: 'Waypoint JSON copied to clipboard!',
        type: 'success'
      });
    }).catch(() => {
      console.log('=== EXPORT seaWaypoints.json ===');
      console.log(json);
      EventBus.emit('ui:notification', {
        message: 'JSON logged to console (clipboard failed)',
        type: 'info'
      });
    });
  }

  /**
   * Save current state to the shared undo stack before making changes.
   * @param {'waypoints'|'ports'} kind  Which "unsaved changes" indicator
   *   this particular edit should light up — the undo snapshot itself
   *   always captures all three arrays regardless, since Ctrl+Z is shared
   *   across both edit types.
   */
  _saveState(kind = 'waypoints') {
    const state = {
      waypoints: JSON.parse(JSON.stringify(this.waypoints)),
      connections: JSON.parse(JSON.stringify(this.connections)),
      ports: JSON.parse(JSON.stringify(this.ports))
    };
    this.undoStack.push(state);
    if (this.undoStack.length > this.maxUndoSteps) {
      this.undoStack.shift();
    }
    if (kind === 'ports') this._markPortsUnsaved();
    else this._markUnsaved();
  }

  /** Restore previous state from undo stack */
  _undo() {
    if (this.undoStack.length === 0) {
      EventBus.emit('ui:notification', {
        message: 'Nothing to undo',
        type: 'info'
      });
      return;
    }

    const state = this.undoStack.pop();
    this.waypoints = state.waypoints;
    this.connections = state.connections;
    this.ports = state.ports;
    this.selectedWaypoint = null;
    this._render();
    this._renderPorts();
    this._updatePortStats();
    // Can't tell from here which of the two this undo step actually
    // touched — conservatively flag both rather than risk a save button
    // looking clean when it isn't.
    this._markUnsaved();
    this._markPortsUnsaved();

    EventBus.emit('ui:notification', {
      message: 'Undone',
      type: 'success'
    });
  }

  /** Mark that there are unsaved WAYPOINT changes */
  _markUnsaved() {
    this.hasUnsavedChanges = true;
    document.getElementById('admin-unsaved').style.display = 'inline';
  }

  /** Clear the waypoint unsaved flag */
  _markSaved() {
    this.hasUnsavedChanges = false;
    document.getElementById('admin-unsaved').style.display = 'none';
    this.undoStack = []; // Clear undo stack after save
  }

  /** Mark that there are unsaved PORT changes */
  _markPortsUnsaved() {
    this.hasUnsavedPortChanges = true;
    document.getElementById('admin-ports-unsaved').style.display = 'inline';
  }

  /** Clear the port unsaved flag */
  _markPortsSaved() {
    this.hasUnsavedPortChanges = false;
    document.getElementById('admin-ports-unsaved').style.display = 'none';
    this.undoStack = []; // Shared undo stack — same tradeoff noted on _markSaved()
  }

  _updatePortStats() {
    document.getElementById('admin-port-stats').textContent = `${this.ports.length} ports loaded`;
    this._renderPortPicker();
  }

  /**
   * Populate the "Ports" section's <select> with every port, sorted by
   * name — an alternative to clicking a port's map marker (see
   * _makePortDraggable()'s docblock: a nearby waypoint or ship frequently
   * sits on the exact same pixel and wins the click, at any zoom level
   * short of zooming in on that one port specifically). Re-rendering the
   * whole <select> loses the current selection, so it's restored by id
   * when still present (e.g. after an edit that didn't rename/delete it).
   */
  _renderPortPicker() {
    const picker = document.getElementById('admin-port-picker');
    if (!picker) return;
    const previousValue = picker.value;
    const sorted = [...this.ports].sort((a, b) => a.name.localeCompare(b.name));
    picker.innerHTML = sorted.map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name)}, ${escapeHtml(p.country)}</option>`).join('');
    if (sorted.some(p => p.id === previousValue)) picker.value = previousValue;
  }

  /** Save waypoints to server */
  async _saveToServer() {
    // Never save an edge to a waypoint that doesn't exist
    const before = this.connections.length;
    const ids = new Set(this.waypoints.map(w => w.id));
    this.connections = this.connections.filter(([a, b]) => ids.has(a) && ids.has(b));
    if (this.connections.length < before) {
      EventBus.emit('ui:notification', {
        message: `Removed ${before - this.connections.length} connection(s) to missing waypoints before saving`,
        type: 'warning'
      });
      this._render();
    }

    const data = {
      waypoints: this.waypoints.map(w => ({
        id: w.id,
        lat: Math.round(w.lat * 1000) / 1000,
        lng: Math.round(w.lng * 1000) / 1000
      })),
      edges: this.connections
    };

    try {
      const response = await fetch('/api/save-waypoints', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        this._markSaved();
        EventBus.emit('ui:notification', {
          message: 'Waypoints saved!',
          type: 'success'
        });
        // Reinitialize routing with new data (seaRoutes expects 'connections')
        initSeaRoutes({ waypoints: data.waypoints, connections: this.connections });
      } else {
        throw new Error('Server returned ' + response.status);
      }
    } catch (e) {
      console.error('Failed to save waypoints:', e);
      EventBus.emit('ui:notification', {
        message: 'Failed to save: ' + e.message,
        type: 'error'
      });
    }
  }

  /**
   * Save port positions to server. Sends every field of every port
   * unchanged, not just id/lat/lng — this.ports holds the exact rows
   * fetched from /data/ports.json (see _loadPorts()), only ever mutated in
   * place on .lat/.lng by _makePortDraggable(), so nothing else about a
   * port (docking fee, produced goods, ...) is at risk of being dropped or
   * reconstructed wrong on the round trip.
   */
  async _savePortsToServer() {
    const data = { ports: this.ports };

    try {
      const response = await fetch('/api/save-ports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        this._markPortsSaved();
        EventBus.emit('ui:notification', {
          message: 'Port positions saved! Takes effect on the next server restart.',
          type: 'success'
        });
      } else {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || ('Server returned ' + response.status));
      }
    } catch (e) {
      console.error('Failed to save ports:', e);
      EventBus.emit('ui:notification', {
        message: 'Failed to save ports: ' + e.message,
        type: 'error'
      });
    }
  }
}
